import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import * as Yup from "yup";
import Modal from "@src/components/common/Modal";
import { ModalSizes } from "@src/components/common/Modal/ModalSizes";
import useYupFieldErrors from "@src/hooks/useYupFieldErrors";
import {
  ClinicUserRoles,
  ClinicUserRole,
} from "@src/constants/ClinicUserConstants";
import * as TeamApi from "@src/api/TeamApi";
import styles from "./InviteMemberModal.module.scss";

const ROLE_OPTIONS: ClinicUserRole[] = [
  ClinicUserRoles.Admin,
  ClinicUserRoles.Member,
];

const schema = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required."),
  lastName: Yup.string().trim().required("Last name is required."),
  email: Yup.string()
    .trim()
    .email("Enter a valid email address.")
    .required("Email is required."),
  role: Yup.mixed<ClinicUserRole>().oneOf(ROLE_OPTIONS).required(),
});

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  role: ClinicUserRole;
}

const INITIAL_DATA: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  role: ClinicUserRoles.Member,
};

interface Props {
  isShowing: boolean;
  onClose: () => void;
  onInvited?: () => void;
}

export default function InviteMemberModal({
  isShowing,
  onClose,
  onInvited,
}: Props): React.JSX.Element {
  const { showToast } = useToast();
  const { formData, handleUpdate, isValid, setFormData } =
    useFormHandlers<FormData>(INITIAL_DATA, schema);
  const { errors, validateField, clearErrors, applyServerFieldErrors } =
    useYupFieldErrors<FormData>(schema);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isShowing) {
      setFormData(INITIAL_DATA);
      clearErrors();
    }
  }, [isShowing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await TeamApi.inviteTeamMember(formData);
      showToast({
        message: `${formData.firstName} ${formData.lastName} added to the team.`,
        variant: "success",
      });
      onInvited?.();
      onClose();
    } catch (error: any) {
      const body = error?.response?.body;
      if (applyServerFieldErrors(body)) return;
      showToast({
        message:
          body?.detail ||
          body?.error ||
          error?.message ||
          "Could not invite team member.",
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isShowing={isShowing}
      onClose={onClose}
      size={ModalSizes.Medium}
      header={<div className={styles.headerTitle}>Add Team Member</div>}
    >
      <div className={styles.fieldStack}>
        <div className={styles.row}>
          <Field error={errors.firstName}>
            <Input
              label="First Name"
              defaultValue=""
              value={formData.firstName}
              onChange={(value: string) =>
                handleUpdate({ name: "firstName", value })
              }
              onBlur={() => validateField("firstName", formData)}
              error={!!errors.firstName}
              width="100%"
              required
              data-testid="invite-member-first-name"
            />
          </Field>
          <Field error={errors.lastName}>
            <Input
              label="Last Name"
              defaultValue=""
              value={formData.lastName}
              onChange={(value: string) =>
                handleUpdate({ name: "lastName", value })
              }
              onBlur={() => validateField("lastName", formData)}
              error={!!errors.lastName}
              width="100%"
              required
              data-testid="invite-member-last-name"
            />
          </Field>
        </div>

        <Field error={errors.email}>
          <Input
            label="Email"
            defaultValue=""
            type="email"
            value={formData.email}
            onChange={(value: string) => handleUpdate({ name: "email", value })}
            onBlur={() => validateField("email", formData)}
            error={!!errors.email}
            width="100%"
            required
            data-testid="invite-member-email"
          />
        </Field>

        <Field error={errors.role}>
          <label className={styles.roleLabel}>Role</label>
          <div className={styles.roleChoices}>
            <label className={styles.roleOption}>
              <input
                type="radio"
                name="role"
                value={ClinicUserRoles.Member}
                checked={formData.role === ClinicUserRoles.Member}
                onChange={() =>
                  handleUpdate({
                    name: "role",
                    value: ClinicUserRoles.Member,
                  })
                }
                data-testid="invite-member-role-member"
              />
              Team Member
            </label>
            <label className={styles.roleOption}>
              <input
                type="radio"
                name="role"
                value={ClinicUserRoles.Admin}
                checked={formData.role === ClinicUserRoles.Admin}
                onChange={() =>
                  handleUpdate({
                    name: "role",
                    value: ClinicUserRoles.Admin,
                  })
                }
                data-testid="invite-member-role-admin"
              />
              Admin
            </label>
          </div>
        </Field>
      </div>

      <Button
        className={`btn btn-primary btn-md ${styles.submitButton}`}
        onClick={handleSubmit}
        disabled={!isValid || isSubmitting}
        data-testid="invite-member-submit"
      >
        {isSubmitting ? "Adding..." : "Add Team Member"}
      </Button>
    </Modal>
  );
}

interface FieldProps {
  error?: string;
  children: React.ReactNode;
}

function Field({ error, children }: FieldProps): React.JSX.Element {
  return (
    <div className={styles.field}>
      {children}
      {error && (
        <p className={styles.errorMessage}>
          <i className="fa-light fa-circle-exclamation" aria-hidden />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
